// src/components/DemandResponseTable.tsx
import * as React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Tooltip from '@mui/material/Tooltip';
import { openResources } from '../authConfig';
import { TableSortLabel } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';


interface Data {
    loadGroup: string;
    do: string[];
    [key: string]: any;
}

interface DemandResponseLogProps {
    coop: string;
    defaultStartDate: Date;
    defaultEndDate: Date
}

const getColor = (value: string) => {
    if (value === 'ON') return 'rgb(0, 95, 0)';
    if (value === 'OFF') return 'rgb(139, 0, 0)';
    return 'inherit';
}

const getTextColor = (value: string) => {
    if (value === 'ON') return 'white';
    if (value === 'OFF') return 'white';
    return 'black';
}

const toolTipDataLoadGroup: Record<string, string> = {
    '1': 'Short-Term Loads (water heaters)',
    '2': 'Intermediate-Term Loads (storage heat)',
    '3': 'Long-Term Loads (dual heating furnaces, back-up generators)',
    '6': 'Summer-Only Loads (irrigation, cycled air conditioning)',
    '9.99': 'Internal Network Transmission',
}

const toolTipData: Record<string, Record<string, string>> = {
    '1.01': {
        '9': 'Grain Dryers',
        '10': 'Pk Ind Light',
        '11': 'Water Heaters',
        '12': 'Water Heaters',
        '13': 'Water Heaters',
        '14': 'Water Heaters',
        '15': 'Clothes Dryer',
        '16': 'Office Heat',
        '17': 'Space Heat',
        '18': 'Space Heat',
        '19': 'Warehouse Fans',
        '20': 'Warehouse Fans',
        '21': 'Warehouse Heat',
        '22': 'Warehouse Heat',
        '23': 'Warehouse Heat',
        '24': 'Warehouse Heat',
    },
    '1.02': {
        '11': 'Water Heaters',
        '12': 'Water Heaters',
        '13': 'Water Heaters',
        '14': 'Water Heaters',
        '15': 'Misc Heat 1',
        '16': 'Misc Heat 1',
        '17': 'Water Heaters',
        '18': 'Water Heaters',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 1',
    },
    '2.01': {
        '9': 'Slab Heat',
        '10': 'Slab Heat',
        '11': 'Slab Heat',
        '12': 'Slab Heat',
        '13': 'Slab Heat',
        '14': 'Slab Heat',
        '15': 'Misc Heat 2',
        '16': 'Misc Heat 2',
        '17': 'Thermal Storage Heat',
        '18': 'Thermal Storage Heat',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '21': 'Thermal Storage Heat',
        '22': 'Thermal Storage Heat',
        '23': 'Thermal Storage Heat',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.02': {
        '12': 'Misc Loads 2',
        '13': 'Misc Loads 2',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.03': {
        '9': 'Slab Heat',
        '10': 'Slab Heat',
        '11': 'Slab Heat',
        '12': 'Slab Heat',
        '13': 'Slab Heat',
        '14': 'Slab Heat',
        '15': 'Misc Heat 2',
        '16': 'Misc Heat 2',
        '17': 'Thermal Storage Heat',
        '18': 'Thermal Storage Heat',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '21': 'Thermal Storage Heat',
        '22': 'Thermal Storage Heat',
        '23': 'Thermal Storage Heat',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.04': {
        '12': 'Misc Loads 2',
        '13': 'Misc Loads 2',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.06': {
        '9': 'Battery Storage',
        '16': 'Battery Storage',
    },
    '3.01': {
        '9': 'Dual Heat',
        '10': 'Dual Heat',
        '11': 'Dual Heat',
        '12': 'Dual Heat',
        '13': 'Dual Heat',
        '14': 'Dual Heat',
        '15': 'Dual Heat',
        '16': 'Dual Heat',
        '17': 'Misc Heat 3',
        '18': 'Misc Heat 3',
        '19': 'Dual Heat',
        '20': 'Dual Heat',
        '21': 'Dual Heat',
        '22': 'Dual Heat',
        '24': 'Ind. Controlled Loads 3',
    },
    '3.06': {
        '9': 'Interruptible Alert',
        '10': 'Industrial Loads',
        '11': 'Commercial',
        '12': 'Commercial',
        '13': 'Commercial',
        '14': 'Commercial',
        '15': 'Industrial',
        '16': 'Commercial',
        '17': 'Commercial',
        '18': 'Commercial',
        '19': 'Commercial',
        '23': 'Ind. Controlled Loads 3',
        '24': 'Ind. Controlled Loads 3',
    },
    '3.07': {
        '9': 'Interruptible Alert',
        '10': 'Comm, Direct Control',
        '11': 'Comm, Direct Control',
        '12': 'Misc Loads 3',
        '13': 'Misc Loads 3',
        '14': 'Comm, Direct Control',
        '15': 'Comm, Direct Control',
        '16': 'Comm, Direct Control',
        '17': 'Misc Loads 3',
        '18': 'Misc Loads 3',
        '19': 'Comm, Direct Control',
        '20': 'Comm, Direct Control',
        '21': 'Ind Controlled Loads 3',
        '22': 'Voltage Reduction',
        '23': 'Comm, Indirect Control',
        '24': 'Ind Controlled Loads 3',
    },
  '3.08':{
    '10':'Industrial Interruptible Loads',
    '15':'Industrial Interruptible Loads',
  },
    '3.09': {
        '9': 'Dual Heat',
        '10': 'Dual Heat',
        '11': 'Dual Heat',
        '12': 'Dual Heat',
        '13': 'Dual Heat',
        '14': 'Dual Heat',
        '15': 'Dual Heat',
        '16': 'Dual Heat',
        '17': 'Misc Heat 3',
        '18': 'Misc Heat 3',
        '24': 'Ind. Contr Loads 3',
    },
  '6.01': {
    '9':'Industrial Interruptible Loads',
    '10':'Low Temperature Grain Dryers',
    '11':'Reserved for Future Use',
    '12':'Miscellaneous Loads',
    '13':'Miscellaneous Loads',
    '14':'Reserved for Future Use',
    '15':'Reserved for Future Use',
    '16':'Reserved for Future Use',
    '17':'Reserved for Future Use',
    '18':'Reserved for Future Use',
    '19':'Reserved for Future Use',
    '20':'Irrigation Incremental Pricing Plan',
    '21':'Residential Locally Cycled Air Conditioning Loads',
    '22':'Commercial Locally Cycled Air Conditioning Loads',
    '23':'Indirectly Controlled Residential Loads',
    '24':'Indirectly Controlled Commercial Loads',
  },
  'Test': {
    '9': 'Test',
    '10': 'Test',
    '11': 'Test',
    '12': 'Test',
    '13': 'Test',
    '14': 'Test',
    '15': 'Test',
    '16': 'Test',
    '17': 'Test',
    '18': 'Test',
    '19': 'Test',
    '20': 'Test',
    '21': 'Test',
    '22': 'Test',
    '24': 'Test',
  }
};

const columnHeaders = [
    "Date",
    "Time",
    "Area",
    "Load Group",
    "DO9", "DO10", "DO11", "DO12", "DO13", "DO14", "DO15",
    "DO16", "DO17", "DO18", "DO19", "DO20", "DO21", "DO22",
    "DO23", "DO24"
];

export const coopCodes = {
    minnkota: "MINN",
    beltrami: "BELT",
    cass: "CASS",
    cavalier: "CAVA",
    cp: "POLK",
    nodak: "NODA",
    northstar: "STAR",
    pkm: "PKME",
    redlake: "REDL",
    redriver: "REDR",
    roseau: "ROSE",
    wildrice: "WILD",
    nmpa: "NMPA"
} as const;

export type CoopKey = keyof typeof coopCodes;
export type CoopCode = typeof coopCodes[CoopKey];

const getToolTipTitle = (loadGroup: string, doubleOrder: string): string => {
    return toolTipData[loadGroup]?.[doubleOrder] ?? 'Unknown';
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        width: 50,
        //style: {text-align: "center"}
        //text-align: "center"
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

export default function DemandResponseLogMobile2({ coop, defaultStartDate, defaultEndDate }: DemandResponseLogProps) {

    const [data, setData] = useState<Data[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [startDate, setStartDate] = React.useState<Date | null>(defaultStartDate);
    const [endDate, setEndDate] = React.useState<Date | null>(defaultEndDate);
    const [hideDatePickers, setHideDatePickers] = useState(true);
    const [orderBy, setOrderBy] = useState<string>('dateTime');
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const coopCode = coopCodes[coop as CoopKey] || 'MINN';

    const sortedData = React.useMemo(() => {
        if (orderBy === 'dateTime') {
            return [...data].sort((a, b) => {
                const dateA = new Date(a.dateTime).getTime();
                const dateB = new Date(b.dateTime).getTime();
                return orderDirection === 'asc' ? dateA - dateB : dateB - dateA;
            });
        }
        return data;
    }, [data, orderBy, orderDirection]);

    useEffect(() => {
        const controller = new AbortController();
        // Function to fetch data from the API
        const fetchData = async () => {
            try {

                const parseDate = (dateString: string) => {
                    //return new Date();
                    const year = `20${dateString.slice(0, 2)}`;
                    const month = dateString.slice(2, 4);
                    const day = dateString.slice(4, 6);
                    const hours = dateString.slice(7, 9);
                    const minutes = dateString.slice(9, 11);
                    return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
                };

                const formatDate = (date: Date) => {
                    return date.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    });
                };

                //`${baseUrl}${endpoint}`
                console.log("Coop:", coop); 
                const response = await axios.get(openResources.ripple.listEndpoint, {
                    params: {
                        area: "nodak", //sessionStorage.getItem('coop'), //|| 'Red River'
                        startDate: startDate ? startDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0], //.split('T')[0]
                        endDate: endDate ? endDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0] //.split('T')[0]
                    },
                    signal: controller.signal
                });
                //console.log("Response data:", endDate); // Debugging line
                if (response.data && Array.isArray(response.data)) {
                    const adaptedData: Data[] = response.data.map((item: any) => ({
                        loadGroup: item.lg,
                        do: item.do,
                        area: item.ac,
                        coop: item.coop,
                        dateTime: formatDate(parseDate(item.datetime)),
                        //date: formatDate(parseDate(item.datetime)),
                        time: item.datetime,
                        err: item.err,
                        name: item.name,
                    }));
                    setData(adaptedData);
                    console.log("Updated log data...");
                } else {
                    setData([]);
                }
                //setPage(0);
            } catch (error: any) {
                if (!controller.signal.aborted) {
                    setError('Error fetching data: ' + error.message);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        const intervalId = setInterval(fetchData, 300000);

        return () => {
            controller.abort();
            clearInterval(intervalId);
        }

    }, [startDate, endDate]);

    useEffect(() => {
        if (data.length === 0 && page > 0) {
            setStartDate((prevDate) => {
                const newDate = new Date(prevDate!);
                newDate.setDate(newDate.getDate() - 1);
                return newDate;
            });
            setEndDate((prevDate) => {
                const newDate = new Date(prevDate!);
                newDate.setDate(newDate.getDate() - 1);
                return newDate;
            });
        }
    }, [data.length, page]);

    useEffect(() => {
        setData([]);
        setPage(0);
        setLoading(true);
    }, [defaultStartDate, defaultEndDate]);

    useEffect(() => {
        setPage(0);
    }, [coop, startDate, endDate]);
    // Avoid a layout jump when reaching the last page with empty rows.
    //const emptyRows =
    //  page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;


    const handleToggle = () => {
        setExportOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setExportOpen(false);
    };

    // Define Load Group Options based on toolTipData keys
    const generateLoadGroupOptions = () => {
        const options = [{ value: 'all', label: 'All Load Groups' }];
        const uniqueLoadGroups = new Set<string>();

        // 1. Collect all unique Load Group keys (e.g., '1.01', '2.04')
        for (const key in toolTipData) {
            if (toolTipData.hasOwnProperty(key)) {
                uniqueLoadGroups.add(key);
            }
        }

        // 2. Sort the keys numerically for better display order
        const sortedKeys = Array.from(uniqueLoadGroups).sort();

        // 3. Map keys to MenuItem format
        sortedKeys.forEach(key => {
            // Find the main category description (e.g., '1' -> 'Short-Term Loads')
            const mainCategory = key.split('.')[0];
            const description = toolTipDataLoadGroup[mainCategory] || 'Specific Load Group';
            
            options.push({
                value: key,
                label: `${key} - ${description}`
            });
        });

        return options;
    };

    const loadGroupOptions = generateLoadGroupOptions();

    // --- Archive Action ---
    const handleArchiveClick = () => {
        const archiveStart = new Date();
        archiveStart.setDate(archiveStart.getDate() - 31); // Example: 30 days ago
        const archiveEnd = new Date();
        setStartDate(archiveStart);
        setEndDate(archiveEnd);
        setHideDatePickers(false); // Show date pickers
        setPage(0);
    };

    // --- Direct Export Action (reusing the existing modal) ---
    const handleExportDirect = () => {
        setExportOpen(false); // Close the menu
        setExportModalOpen(true); // Open the date range modal for export
    };

    const handleExportClick = () => {
       // If the Archive date pickers are visible, use that range directly.
        if (!hideDatePickers && startDate && endDate) {
            // Use the existing, currently viewed date range for the export
            //fetchAndExportData(startDate, endDate);
        } else {
            // If viewing "Today" or "Yesterday", open the modal to allow a wider range selection.
            setExportModalOpen(true);
        }
    };

    const fetchAndExportData = async (exportStart: Date | null, exportEnd: Date | null, loadGroup: string | null) => {
        // 1. Prepare API parameters for a full dataset export
        setLoading(true);
        if(!exportStart) exportStart = new Date();
        if(!exportEnd) exportEnd = new Date();

        const startParam = exportStart.toISOString().split('T')[0];
        const endParam = exportEnd.toISOString().split('T')[0];

        const params: Record<string, any> = {
            area: coop,
            startDate: startParam,
            endDate: endParam,
        };

        if(loadGroup && loadGroup !== 'all'){
            params.loadGroup = loadGroup;
        }

        try {
            // 2. Fetch ALL data for the export range (might be a different endpoint or parameter)
            const response = await axios.get(openResources.ripple.listEndpoint, {
                params: //{
                    params
                    // Add a parameter to get ALL data, not paginated, if your API supports it
                    // e.g., pagination: false, rowsPerPage: -1 
                //}
            });

            const rawData = response.data;
            if (rawData && Array.isArray(rawData)) {
                console.log(rawData);
                // 3. Convert the raw JSON data to a CSV string
                const csv = convertRawDataToCSV(rawData); 
                // 4. Trigger the download in the browser
                downloadCSV(csv, 'demand_response_export.csv');
            }
            // Close the modal upon success
        } catch (error) {
            // Handle error (e.g., show a toast/snackbar)
            console.error("Export failed:", error);
        }
        setLoading(false);
    };

    // Add this or similar utility functions outside the component function:
    const convertRawDataToCSV = (rawData: any[]): string => {
        // These headers MUST match the keys/fields in the row map below.
        const exportHeaders = ["Date Time", "Area", "Load Group", "DO9", "DO10", "DO11", "DO12", "DO13", "DO14", "DO15", "DO16", "DO17", "DO18", "DO19", "DO20", "DO21", "DO22", "DO23", "DO24"];
        
        const rows = rawData.map(item => {
            // Re-use your date formatting logic for consistency
            const parseDate = (dateString: string) => {
                const year = `20${dateString.slice(0, 2)}`;
                const month = dateString.slice(2, 4);
                const day = dateString.slice(4, 6);
                const hours = dateString.slice(7, 9);
                const minutes = dateString.slice(9, 11);
                return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
            };
            const formatDate = (date: Date) => date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });

            const dateTimeFormatted = formatDate(parseDate(item.datetime));
            const doValues = item.do || []; // Ensure 'do' array exists
            
            const rowData = [
                dateTimeFormatted,
                item.ac, // Area
                item.lg, // Load Group
                ...doValues // Spreads DO9 to DO24
            ];
            return rowData.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
        });
    
        return [exportHeaders.join(','), ...rows].join('\n');
    };

    const downloadCSV = (csvString: string, filename: string) => {
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        // Create a temporary link and click it to start the download
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    interface ExportModalProps {
        open: boolean;
        onClose: () => void;
        onExport: (start: Date | null, end: Date | null, loadgroup: string | null) => void;
        defaultStart: Date | null;
        defaultEnd: Date | null;
        loadGroupOptions: { value: string; label: string }[];
    }

    const ExportDateRangeModal: React.FC<ExportModalProps> = ({ open, onClose, onExport, defaultStart, defaultEnd }) => {
        const [modalStartDate, setModalStartDate] = useState<Date | null>(defaultStart);
        const [modalEndDate, setModalEndDate] = useState<Date | null>(defaultEnd);
        const [selectedLoadGroup, setSelectedLoadGroup] = useState<string>('all');

        const handleLoadGroupChange = (event: SelectChangeEvent<string>) => {
            setSelectedLoadGroup(event.target.value);
        };

        const handleExport = () => {
            if (modalStartDate && modalEndDate) {
                onExport(modalStartDate, modalEndDate, selectedLoadGroup);
            } else {
                alert("Please select both a start and end date for the export.");
            }
        };

        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>Select Date Range for CSV Export</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <DatePicker
                                label="Export Start Date"
                                value={modalStartDate}
                                onChange={(newValue) => setModalStartDate(newValue)}
                            />
                            <DatePicker
                                label="Export End Date"
                                value={modalEndDate}
                                onChange={(newValue) => setModalEndDate(newValue)}
                            />
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <InputLabel id="load-group-select-label">Load Group</InputLabel>
                                <Select
                                    labelId="load-group-select-label"
                                    id="load-group-select"
                                    value={selectedLoadGroup}
                                    label="Load Group"
                                    onChange={handleLoadGroupChange}
                                >
                                    {loadGroupOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                        </FormControl>
                        </Box>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button onClick={handleExport} variant="contained" color="primary">
                        Export CSV
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

return (
    // 1. ROOT CONTAINER
    // - minHeight: 100vh ensures full page height.
    // - display: block (default) allows natural flow.
    // - overflow: visible (default) is CRITICAL. Do not add overflow here.
    <div style={{ maxWidth: 2100, margin: 'auto', minHeight: '100vh', paddingBottom: '50px' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            
            {/* --- Header Section (Buttons & DatePickers) --- */}
            {/* This section scrolls away naturally as you scroll down */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <ButtonGroup variant="contained">
                    <Button onClick={() => {
                        const today = new Date();
                        setStartDate(today);
                        setEndDate(today);
                        setHideDatePickers(true);
                        setPage(0);
                    }}
                    style={{ backgroundColor: startDate?.toDateString() === new Date().toDateString() ? '#007BFF' : '#1E5AB9' }}>
                        Today
                    </Button>
                    <Button onClick={() => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        setStartDate(yesterday);
                        setEndDate(yesterday);
                        setHideDatePickers(true);
                        setPage(0);
                    }}
                    style={{ backgroundColor: startDate?.toDateString() === new Date(new Date().setDate(new Date().getDate() - 1)).toDateString() ? '#007BFF' : '#1E5AB9' }}>
                        Yesterday
                    </Button>
                    <React.Fragment>
                        <div ref={anchorRef} style={{ display: 'flex' }}>
                            <Button 
                                onClick={handleArchiveClick} 
                                style={{
                                    backgroundColor: startDate && endDate && startDate < new Date(new Date().setDate(new Date().getDate() - 31)) ? '#007BFF' : '#1E5AB9',
                                    borderRight: '1px solid rgba(255, 255, 255, 0.25)', 
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                }}
                            >
                                Archive
                            </Button>
                            <Button
                                size="small"
                                onClick={handleToggle}
                                style={{ 
                                    backgroundColor: '#1E5AB9', 
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    minWidth: 'auto', 
                                    padding: '6px 4px', 
                                }}
                            >
                                <ArrowDropDownIcon />
                            </Button>
                        </div>
                    </React.Fragment>
                </ButtonGroup>
                
                <Popper open={exportOpen} anchorEl={anchorRef.current} role={undefined} transition disablePortal sx={{ zIndex: 1200 }}>
                    {({ TransitionProps, placement }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList><MenuItem onClick={handleExportDirect} disabled={loading}><CloudDownloadIcon sx={{ mr: 1 }} /> Export to CSV...</MenuItem></MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2, visibility: hideDatePickers ? 'hidden' : 'visible', height: hideDatePickers ? 0 : 'auto', overflow: 'hidden', paddingTop: '5px', }}>
                <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} />
                <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} />
            </Box>

            {/* --- TABLE CONTAINER --- */}
            {/* 2. TABLE WRAPPER 
               - width: 100% ensures it spans the page.
               - overflow: visible ensures the table spills out naturally so the WINDOW handles the scroll.
            */}
            <Box sx={{ width: '100%', overflow: 'visible' }}>
                <TableContainer component={Paper} sx={{ maxHeight: 'none', boxShadow: 'none', overflow: 'visible', borderRadius: 0 }}>
                    <Table size='small' sx={{ minWidth: 1000 }} aria-label="custom pagination table">
                        <TableHead>
                            <StyledTableRow>
                                {columnHeaders.map((header, index) => {
                                    const isSortable = header === 'Date Time';
                                    const sortKey = 'dateTime';
                                    return (
                                        // 3. STICKY HEADER
                                        // Applied to the TH cell directly.
                                        // 'top: 0' sticks it to the top of the browser window.
                                        <StyledTableCell 
                                            key={index} 
                                            style={{ 
                                                textAlign: 'center',
                                                position: 'sticky', 
                                                top: 0, 
                                                zIndex: 1000, 
                                                // 4. BACKGROUND COLOR
                                                // Essential! Without this, the row data will scroll visibly *behind* the header text.
                                                backgroundColor: '#1E5AB9', 
                                                color: 'white',
                                                boxShadow: '0 2px 2px -1px rgba(0,0,0,0.4)' 
                                            }}
                                        >
                                            {isSortable ? (
                                                <TableSortLabel
                                                    active={orderBy === sortKey}
                                                    direction={orderDirection}
                                                    onClick={() => {
                                                        const isAsc = orderBy === sortKey && orderDirection === 'asc';
                                                        setOrderDirection(isAsc ? 'desc' : 'asc');
                                                        setOrderBy(sortKey);
                                                    }}
                                                    sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 }}}
                                                >
                                                    {header}
                                                </TableSortLabel>
                                            ) : ( header )}
                                        </StyledTableCell>
                                    );
                                })}
                            </StyledTableRow>
                        </TableHead>

                        <TableBody>
                            {(rowsPerPage > 0 ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : data).map((row) => (
                                <StyledTableRow key={`${row.loadGroup}-${row.time}`}>
                                    <StyledTableCell component="th" scope="row" style={{ textAlign: 'center' }}>{new Date(row.dateTime).toLocaleDateString('en-US')}
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row" style={{ textAlign: 'center' }}>{new Date(row.dateTime).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row" style={{ textAlign: 'center' }}>{row.area}</StyledTableCell>
                                    <StyledTableCell component="th" scope="row" style={{ textAlign: 'center' }}>
                                        <Tooltip title={toolTipDataLoadGroup[row.loadGroup.replace(/\.\d+$/, "")] || 'Unknown'}><span>{row.loadGroup}</span></Tooltip>
                                    </StyledTableCell>
                                    {row.do.map((value, index) => (
                                        <StyledTableCell key={index} style={{ backgroundColor: getColor(value), color: getTextColor(value), textAlign: 'center' }}>
                                            <Tooltip title={getToolTipTitle(row.loadGroup, (index + 9).toString())}><span>{value}</span></Tooltip>
                                        </StyledTableCell>
                                    ))}
                                </StyledTableRow>
                            ))}
                            {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={6} /></TableRow>}
                        </TableBody>

                        <TableFooter>
                            <TableRow>
                                {/* 5. STICKY FOOTER 
                                    Sticks to the bottom of the window so pagination is always accessible.
                                */}
                                {/*<TableCell component="div"
                                    colSpan={columnHeaders.length} sx={{ 
                                        p: 0, 
                                        borderBottom: 'none', 
                                        position: 'sticky', 
                                        bottom: 0, 
                                        zIndex: 1000, 
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #ddd',
                                }}>
                                    {/*<Box component="div" sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    */}
                                        <TablePagination
                                            rowsPerPageOptions={[20, 40, 60, { label: 'All', value: -1 }]}
                                            count={data.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            slotProps={{ select: { inputProps: { 'aria-label': 'rows per page' }, native: true } }}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                            sx={{ flex: 1, position: 'sticky',p: 0, 
                                        borderBottom: 'none',
                                        bottom: 0, 
                                        zIndex: 1000, 
                                        backgroundColor: 'white',
                                        borderTop: '1px solid #ddd', }}
                                        />
                                    {/*</Box>*/}
                                {/*</TableCell>*/}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Box>

            {/* --- Status Boxes --- */}
            <Box sx={{ mt: 2, px: 2, py: 1, width: '100%' }}> 
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, flexWrap: 'wrap'}}>
                    <div className="load-group-container">
                        <div className="load-group-categories" >
                            <h3>{"Load Group Categories"}</h3>
                            <p>{"1 : Short-Term Loads(water heaters)"}</p>
                            <p>{"2 : Intermediate-Term Loads (storage heat)"}</p>
                            <p>{"3 : Long-Term Loads (dual heating furnaces, back-up generators)"}</p>
                            <p>{"6 : Summer-Only Loads (irrigation, cycled air conditioning)"}</p>
                            <p>{"9.99 : Internal Network Transmission"}</p>
                        </div>
                        <div className="load-group-categories" >
                            <h3>{"Status"}</h3>
                            <p>{"ON : Device is not being controlled."}</p>
                            <p>{"OFF : Device is being controlled."}</p>
                            <p>{"--- : N/A or status defaults to previous state."}</p>
                        </div>
                    </div>
                </Box>
            </Box>

            <ExportDateRangeModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={fetchAndExportData}
                defaultStart={startDate}
                defaultEnd={endDate}
                loadGroupOptions={loadGroupOptions}
            />
        </LocalizationProvider>
    </div>
);
}